import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { functionsService } from '../services/api';
import { getAxiosErrorMessage } from '../utils/errorUtils';
import { Function } from '../types';
import { ArrowLeft, Save, FileText, Plus, Trash2, File, FileCode, FileImage, FileVideo, FileAudio, FileArchive, FileSpreadsheet, Copy, Circle, RotateCw, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import Editor from '@monaco-editor/react';

const EditFunction: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [functionData, setFunctionData] = useState<Function | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [newFileName, setNewFileName] = useState('');
  const [showNewFileInput, setShowNewFileInput] = useState(false);
  const [containerStatus, setContainerStatus] = useState<'healthy' | 'unhealthy' | 'not_running' | 'loading' | 'deploying'>('loading');
  const [restarting, setRestarting] = useState(false);
  const [editingFilename, setEditingFilename] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      loadFunction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFunction wird bei id-Änderung explizit aufgerufen
  }, [id]);

  const loadFunction = async () => {
    try {
      const data = await functionsService.getById(id!);
      setFunctionData(data);
      await loadFiles();
    } catch (_error) {
      toast.error('Fehler beim Laden der Function');
      navigate('/');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      // Wait a bit for authentication to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const fileList = await functionsService.getFiles(id!);
      setFiles(fileList);
      
      // If no files exist, create a default index.ts
      if (fileList.length === 0) {
        await createDefaultFile();
      } else {
        // Select index.ts by default, fallback to first .ts or .js file
        const indexFile = fileList.find(file => file === 'index.ts');
        const tsFile = fileList.find(file => file.endsWith('.ts') || file.endsWith('.js'));
        const fileToSelect = indexFile || tsFile || fileList[0];
        setSelectedFile(fileToSelect);
        await loadFileContent(fileToSelect);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      // Retry after a longer delay if authentication failed
      setTimeout(async () => {
        try {
          const fileList = await functionsService.getFiles(id!);
          setFiles(fileList);
          if (fileList.length > 0) {
            // Select index.ts by default, fallback to first .ts or .js file
            const indexFile = fileList.find(file => file === 'index.ts');
            const tsFile = fileList.find(file => file.endsWith('.ts') || file.endsWith('.js'));
            const fileToSelect = indexFile || tsFile || fileList[0];
            setSelectedFile(fileToSelect);
            await loadFileContent(fileToSelect);
          }
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          toast.error('Fehler beim Laden der Dateien');
        }
      }, 3000);
    }
  };

  // Force select index.ts when files change
  useEffect(() => {
    if (files.length > 0 && !selectedFile) {
      const indexFile = files.find(file => file === 'index.ts');
      const tsFile = files.find(file => file.endsWith('.ts') || file.endsWith('.js'));
      const fileToSelect = indexFile || tsFile || files[0];
      setSelectedFile(fileToSelect);
      loadFileContent(fileToSelect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei files-Änderung ausführen, nicht bei selectedFile
  }, [files]);

  // Container-Status pollen (alle 10 Sekunden)
  useEffect(() => {
    if (!id) return;
    const fetchHealth = async () => {
      try {
        const { status } = await functionsService.getHealth(id);
        setContainerStatus(status);
      } catch {
        setContainerStatus('unhealthy');
      }
    };
    setContainerStatus('loading');
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, [id]);

  const createDefaultFile = async () => {
    const defaultContent = `import { serve } from 'https://deno.land/std@0.168.0/http/server.ts';

const handler = async (req: Request): Promise<Response> => {
  try {
    const { method, url } = req;
    
    if (method === 'GET') {
      return new Response(JSON.stringify({ 
        message: 'Hello from ${functionData?.name || 'function'}!',
        timestamp: new Date().toISOString()
      }), {
        headers: { 'Content-Type': 'application/json' }
      });
    }
    
    if (method === 'POST') {
      const body = await req.json();
      return new Response(JSON.stringify({ 
        received: body,
        timestamp: new Date().toISOString()
      }), {
        headers: { 'Content-Type': 'application/json' }
      });
    }
    
    return new Response('Method not allowed', { status: 405 });
  } catch (error) {
    return new Response(JSON.stringify({ error: error.message }), { 
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }
};

serve(handler);`;

    try {
      await functionsService.updateFile(id!, 'index.ts', defaultContent);
      setFiles(['index.ts']);
      setSelectedFile('index.ts');
      setFileContent(defaultContent);
    } catch (error) {
      console.error('Error creating default file:', error);
    }
  };

  const loadFileContent = async (filename: string) => {
    try {
      const response = await functionsService.getFileContent(id!, filename);
      setFileContent(response.content);
    } catch (error) {
      console.error('Error loading file content:', error);
      toast.error('Fehler beim Laden der Datei');
    }
  };

  const handleFileSelect = async (filename: string) => {
    setSelectedFile(filename);
    await loadFileContent(filename);
  };

  const handleSaveFile = async () => {
    if (!selectedFile) return;

    const isMainFile = selectedFile === 'index.ts' || selectedFile === 'function.ts';
    setLoading(true);
    try {
      await functionsService.updateFile(id!, selectedFile, fileContent);
      toast.success('Datei erfolgreich gespeichert!');
      if (isMainFile) {
        setContainerStatus('deploying');
        // Poll für Status nach Redeploy (Build dauert 30–60 Sekunden)
        const pollInterval = 5000;
        const maxAttempts = 24; // 2 Minuten
        let attempts = 0;
        const poll = async () => {
          if (attempts >= maxAttempts) return;
          attempts++;
          try {
            const { status } = await functionsService.getHealth(id!);
            setContainerStatus(status);
            if (status === 'healthy') return; // Deploy abgeschlossen
          } catch {
            setContainerStatus('not_running');
          }
          if (attempts < maxAttempts) setTimeout(poll, pollInterval);
        };
        setTimeout(poll, pollInterval);
      }
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Speichern der Datei'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFile = async () => {
    if (!newFileName.trim()) {
      toast.error('Bitte geben Sie einen Dateinamen ein');
      return;
    }

    if (files.includes(newFileName)) {
      toast.error('Eine Datei mit diesem Namen existiert bereits');
      return;
    }

    try {
      await functionsService.updateFile(id!, newFileName, '');
      setFiles([...files, newFileName]);
      setSelectedFile(newFileName);
      setFileContent('');
      setNewFileName('');
      setShowNewFileInput(false);
      toast.success('Datei erfolgreich erstellt!');
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Erstellen der Datei'));
    }
  };

  const handleDeleteFile = async (filename: string) => {
    if (files.length <= 1) {
      toast.error('Die letzte Datei kann nicht gelöscht werden');
      return;
    }

    if (!confirm(`Möchten Sie die Datei "${filename}" wirklich löschen?`)) {
      return;
    }

    try {
      await functionsService.deleteFile(id!, filename);
      setFiles(files.filter(f => f !== filename));
      
      if (selectedFile === filename) {
        const remainingFiles = files.filter(f => f !== filename);
        if (remainingFiles.length > 0) {
          setSelectedFile(remainingFiles[0]);
          await loadFileContent(remainingFiles[0]);
        }
      }
      
      toast.success('Datei erfolgreich gelöscht!');
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Löschen der Datei'));
    }
  };

  const handleCopyUrl = async () => {
    try {
      const { url } = await functionsService.getUrl(id!);
      await navigator.clipboard.writeText(url);
      toast.success('URL in die Zwischenablage kopiert');
    } catch (_error) {
      toast.error('Fehler beim Kopieren der URL');
    }
  };

  const startRenameFile = (filename: string) => {
    setEditingFilename(filename);
    setEditNameValue(filename);
  };

  const cancelRenameFile = () => {
    setEditingFilename(null);
    setEditNameValue('');
  };

  const handleRenameFile = async () => {
    if (!id || !editingFilename || !editNameValue.trim()) {
      cancelRenameFile();
      return;
    }
    const newName = editNameValue.trim();
    if (newName === editingFilename) {
      cancelRenameFile();
      return;
    }
    if (files.includes(newName)) {
      toast.error('Eine Datei mit diesem Namen existiert bereits');
      return;
    }
    const isMainFile = editingFilename === 'index.ts' || editingFilename === 'function.ts' || newName === 'index.ts' || newName === 'function.ts';
    setLoading(true);
    try {
      await functionsService.renameFile(id, editingFilename, newName);
      setFiles(files.map(f => f === editingFilename ? newName : f));
      if (selectedFile === editingFilename) setSelectedFile(newName);
      cancelRenameFile();
      toast.success('Datei erfolgreich umbenannt');
      if (isMainFile) {
        setContainerStatus('deploying');
        const pollInterval = 5000;
        const maxAttempts = 24;
        let attempts = 0;
        const poll = async () => {
          if (attempts >= maxAttempts) return;
          attempts++;
          try {
            const { status } = await functionsService.getHealth(id);
            setContainerStatus(status);
            if (status === 'healthy') return;
          } catch {
            setContainerStatus('not_running');
          }
          if (attempts < maxAttempts) setTimeout(poll, pollInterval);
        };
        setTimeout(poll, pollInterval);
      }
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Umbenennen der Datei'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = async () => {
    if (!id) return;
    setRestarting(true);
    try {
      await functionsService.restart(id);
      setContainerStatus('loading');
      const { status } = await functionsService.getHealth(id);
      setContainerStatus(status);
      toast.success('Container neu gestartet');
    } catch (_error) {
      toast.error('Fehler beim Neustart des Containers');
    } finally {
      setRestarting(false);
    }
  };


  const getLanguage = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'json':
        return 'json';
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      default:
        return 'plaintext';
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const iconProps = { className: "h-4 w-4", style: { color: '#6b7280' } };
    
    switch (ext) {
      case 'ts':
      case 'tsx':
      case 'js':
      case 'jsx':
        return <FileCode {...iconProps} />;
      case 'py':
      case 'go':
      case 'rs':
      case 'java':
      case 'cpp':
      case 'cc':
      case 'cxx':
      case 'c':
      case 'cs':
      case 'php':
      case 'rb':
      case 'swift':
      case 'kt':
      case 'scala':
        return <FileCode {...iconProps} />;
      case 'html':
      case 'css':
      case 'scss':
      case 'sass':
        return <FileCode {...iconProps} />;
      case 'json':
      case 'xml':
      case 'yaml':
      case 'yml':
        return <FileCode {...iconProps} />;
      case 'md':
        return <FileText {...iconProps} />;
      case 'sql':
      case 'sh':
      case 'bash':
      case 'dockerfile':
        return <FileCode {...iconProps} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
      case 'webp':
        return <FileImage {...iconProps} />;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return <FileVideo {...iconProps} />;
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
        return <FileAudio {...iconProps} />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FileArchive {...iconProps} />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <FileSpreadsheet {...iconProps} />;
      default:
        return <File {...iconProps} />;
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!functionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Function nicht gefunden</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 btn btn-primary"
          >
            Zurück zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Action Bar - Always Visible */}
      <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: 'white', padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/')}
              style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '14px' }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück
            </button>
            <div style={{ height: '20px', width: '1px', backgroundColor: '#e5e7eb' }}></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <h1 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                {functionData.name}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'monospace' }}>
                  {`https://functions.digitalaisolutions.de/api/execute/${functionData.id}`}
                </span>
                <button
                  onClick={handleCopyUrl}
                  style={{ 
                    padding: '4px', 
                    color: '#9ca3af',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px'
                  }}
                  title="URL kopieren"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              title={
                containerStatus === 'healthy'
                  ? 'Container läuft'
                  : containerStatus === 'unhealthy'
                  ? 'Container antwortet nicht'
                  : containerStatus === 'not_running'
                  ? 'Container gestoppt'
                  : containerStatus === 'deploying'
                  ? 'Container wird neu deployt...'
                  : 'Prüfe Status...'
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor:
                  containerStatus === 'healthy'
                    ? '#dcfce7'
                    : containerStatus === 'unhealthy'
                    ? '#fef3c7'
                    : containerStatus === 'not_running'
                    ? '#fee2e2'
                    : containerStatus === 'deploying'
                    ? '#dbeafe'
                    : '#f3f4f6',
                color:
                  containerStatus === 'healthy'
                    ? '#166534'
                    : containerStatus === 'unhealthy'
                    ? '#b45309'
                    : containerStatus === 'not_running'
                    ? '#dc2626'
                    : containerStatus === 'deploying'
                    ? '#2563eb'
                    : '#6b7280',
                border: `1px solid ${
                  containerStatus === 'healthy'
                    ? '#bbf7d0'
                    : containerStatus === 'unhealthy'
                    ? '#fde68a'
                    : containerStatus === 'not_running'
                    ? '#fecaca'
                    : containerStatus === 'deploying'
                    ? '#93c5fd'
                    : '#e5e7eb'
                }`,
                cursor: 'default',
              }}
            >
              <Circle
                className="h-3 w-3"
                style={{
                  fill: containerStatus === 'healthy' ? '#22c55e' : containerStatus === 'not_running' ? '#dc2626' : containerStatus === 'unhealthy' ? '#f59e0b' : containerStatus === 'deploying' ? '#2563eb' : '#9ca3af',
                  minWidth: '12px',
                }}
              />
              {containerStatus === 'healthy'
                ? 'Läuft'
                : containerStatus === 'unhealthy'
                ? 'Fehler'
                : containerStatus === 'not_running'
                ? 'Gestoppt'
                : containerStatus === 'deploying'
                ? 'Deployt...'
                : '...'}
            </span>
            {(containerStatus === 'unhealthy' || containerStatus === 'not_running') && (
              <button
                onClick={handleRestart}
                disabled={restarting}
                title="Container neu starten"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#eff6ff',
                  color: '#2563eb',
                  borderRadius: '6px',
                  border: '1px solid #dbeafe',
                  cursor: restarting ? 'not-allowed' : 'pointer',
                  opacity: restarting ? 0.6 : 1,
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                <RotateCw className="h-4 w-4 mr-1" style={{ animation: restarting ? 'spin 1s linear infinite' : undefined }} />
                {restarting ? 'Starte...' : 'Neustart'}
              </button>
            )}
            <button
              onClick={handleSaveFile}
              disabled={loading || !selectedFile}
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#2563eb', 
                color: 'white', 
                fontSize: '14px', 
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                opacity: (loading || !selectedFile) ? 0.5 : 1
              }}
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Speichere...' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - Side by Side Layout */}
      <div style={{ flex: 1, display: 'flex', height: 'calc(100vh - 80px)' }}>
        {/* Left Sidebar - File Explorer (Fixed Width) */}
        <div style={{ width: '320px', backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
          {/* Files Header */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>FILES</h3>
              <button
                onClick={() => setShowNewFileInput(!showNewFileInput)}
                style={{ padding: '4px', color: '#9ca3af' }}
                title="Neue Datei hinzufügen"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {showNewFileInput && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  placeholder="Dateiname eingeben..."
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFile()}
                />
                <button
                  onClick={handleCreateFile}
                  style={{ padding: '8px 12px', backgroundColor: '#2563eb', color: 'white', borderRadius: '6px', fontSize: '14px' }}
                >
                  Erstellen
                </button>
              </div>
            )}
          </div>

          {/* Files List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ padding: '8px' }}>
              {files.map((filename) => (
                <div
                  key={filename}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedFile === filename ? '#eff6ff' : 'transparent',
                    border: selectedFile === filename ? '1px solid #dbeafe' : '1px solid transparent'
                  }}
                  onClick={() => !editingFilename && handleFileSelect(filename)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <div style={{ width: '16px', height: '16px', marginRight: '12px', flexShrink: 0 }}>
                      {getFileIcon(editingFilename === filename ? editNameValue : filename)}
                    </div>
                    {editingFilename === filename ? (
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameFile();
                          if (e.key === 'Escape') cancelRenameFile();
                        }}
                        onBlur={() => {
                          if (editNameValue.trim() === '' || editNameValue.trim() === editingFilename) {
                            cancelRenameFile();
                          } else {
                            handleRenameFile();
                          }
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          flex: 1,
                          padding: '4px 8px',
                          fontSize: '14px',
                          border: '1px solid #2563eb',
                          borderRadius: '4px',
                          minWidth: 0,
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '14px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {filename}
                      </span>
                    )}
                  </div>
                  {!editingFilename && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startRenameFile(filename);
                        }}
                        style={{ padding: '4px', color: '#9ca3af', marginRight: '4px' }}
                        title="Datei umbenennen"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile(filename);
                        }}
                        style={{ padding: '4px', color: '#9ca3af' }}
                        title="Datei löschen"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Code Editor (Takes remaining space) */}
        <div style={{ flex: 1, backgroundColor: 'white' }}>
          {selectedFile ? (
            <div style={{ height: '100%' }}>
              <Editor
                height="100%"
                language={getLanguage(selectedFile)}
                value={fileContent}
                onChange={(value) => setFileContent(value || '')}
                theme="vs-light"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  roundedSelection: false,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: 'on',
                  padding: { top: 16, bottom: 16 },
                }}
              />
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <FileText className="h-12 w-12 mx-auto mb-4" style={{ color: '#d1d5db' }} />
                <p style={{ fontSize: '14px' }}>Wählen Sie eine Datei aus, um sie zu bearbeiten</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditFunction;